'use client';

import { useState } from 'react';
import { useFuzzySearchList } from '@nozbe/microfuzz/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Group } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { Spotlight } from '@mantine/spotlight';
import { useCurrentAgency } from '@/queries/agencies/useCurrentAgency';
import { useClients } from '@/queries/clients/useClients';
import { Icon } from '@/ui';
import { ClientBadge } from '../Clients/ClientBadge/ClientBadge';
import { searchStore } from './store';

export const SearchSpotlight = function SearchSpotlight() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 300);
  const { t } = useTranslation();
  const { data: clientsData } = useClients();
  const { data: agency } = useCurrentAgency();
  const isClientUser = agency?.role === 'client';

  // TODO: posts search was backed by the MobX posts store, now removed. Wire this up
  // to a React Query-based post search before re-enabling post results in search.
  const posts: { id: string; type: string; searchText: string[] }[] = [];

  const clients = (clientsData?.data.clients ?? []).map((client) => ({
    id: client.id,
    type: 'client',
    searchText: [client.name],
  }));

  const filteredList = useFuzzySearchList({
    list: [...clients, ...posts],
    queryText: debouncedSearchTerm,
    getText: (item) => item.searchText,
    mapResultItem: ({ item }) => item,
  });

  return (
    <Spotlight.Root
      store={searchStore}
      shortcut={['mod + K', 'mod + P', '/']}
      query={searchTerm}
      onQueryChange={setSearchTerm}
      size="xl"
    >
      <Spotlight.Search
        placeholder={t('search.placeholder')}
        leftSection={<Icon name="search" />}
      />
      <Spotlight.ActionsList>
        {filteredList.length ? (
          filteredList.slice(0, 7).map((result) => {
            if (result.type === 'post') {
              return null;
            }

            if (result.type === 'client') {
              const client = clientsData?.data.clients.find((c) => c.id === result.id);
              if (!client) {
                return null;
              }

              return (
                <Spotlight.Action
                  key={result.id}
                  label={client.name}
                  onClick={() =>
                    navigate(isClientUser ? `/client-area/${result.id}` : `/client/${result.id}`)
                  }
                  style={{
                    '&:hover': {
                      backgroundColor: 'var(--mantine-color-gray-1)',
                    },
                  }}
                >
                  <Group wrap="nowrap" w="100%" gap="3xs">
                    <ClientBadge clientId={client.id} />
                  </Group>
                </Spotlight.Action>
              );
            }

            return null;
          })
        ) : (
          <Spotlight.Empty>{t('search.no-results')}</Spotlight.Empty>
        )}
      </Spotlight.ActionsList>
    </Spotlight.Root>
  );
};
